import { useAxios } from '@healthblocks-io/core'
import { isAxiosError } from 'axios'
import { useState } from 'react'

import { useLog } from '@/@bothrs-react/hooks/useLog'
import { getMedicationPhotoFileIds } from '@/modules/app/utils/medication.utils'

import type { MedicationScheme } from '@/modules/app/types/HealthParameter.types'

/**
 * Deletes a stored medication file the patient removes, so a wrong upload does
 * not stay stored.
 *
 * Only a file added since the scheme was opened. One the scheme came with, from
 * an earlier answer or a saved draft, may still be named by an answer that was
 * submitted, and may not have reached the care team yet: removing it only takes
 * it off the scheme. The file is off the scheme either way, so a failed delete
 * only leaves it stored, and is logged.
 *
 * ponytail: a file uploaded in a saved draft and removed after reopening it
 * stays stored. Delete those on submit if that ever adds up.
 */
export function useDeleteMedicationFile(scheme: MedicationScheme | undefined) {
  const axios = useAxios()
  const { logAppInsightsWarning } = useLog()
  // Frozen on the first render, which already holds a prefilled scheme: no value
  // then means the scheme came with no files, and its first file is one the
  // patient adds.
  const [openedWith] = useState(
    () => new Set(getMedicationPhotoFileIds(scheme?.medications ?? [], scheme?.files ?? [])),
  )

  return (fileId: string) => {
    if (openedWith.has(fileId)) {
      return
    }

    axios.delete(`/file/${encodeURIComponent(fileId)}`).catch((error) => {
      logAppInsightsWarning('medication file delete failed', {
        event: 'medication_file.delete_failed',
        fileId,
        status: isAxiosError(error) ? error.response?.status : undefined,
      })
    })
  }
}
